import { useState, useEffect } from "react";
import { NavigationHeader } from "@/components/NavigationHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { auth } from "@/firebase";
import { Loader2, Plus, Trash2, Key, Copy, Eye, EyeOff } from "lucide-react";

interface ApiToken {
    id: string;
    name: string;
    token: string;
    createdAt: any;
    lastUsedAt: any;
    usageCount: number;
    active: boolean;
}

export default function ApiTokens({ embedded = false }: { embedded?: boolean }) {
    const [tokens, setTokens] = useState<ApiToken[]>([]);
    const [loading, setLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [newTokenName, setNewTokenName] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [generatedToken, setGeneratedToken] = useState<string | null>(null);
    const [visibleTokens, setVisibleTokens] = useState<Record<string, boolean>>({});

    const { toast } = useToast();

    const fetchTokens = async () => {
        try {
            const user = auth.currentUser;
            if (!user) return;
            const idToken = await user.getIdToken();

            const response = await fetch("/admin-api/admin/api-tokens", {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            });

            if (!response.ok) throw new Error("Falha ao carregar tokens");

            const data = await response.json();
            setTokens(data);
        } catch (error) {
            console.error(error);
            toast({
                title: "Erro",
                description: "Não foi possível carregar os tokens de API.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTokens();
    }, []);

    const handleCreateToken = async () => {
        if (!newTokenName.trim()) return;
        setIsCreating(true);
        setGeneratedToken(null);

        try {
            const user = auth.currentUser;
            if (!user) return;
            const idToken = await user.getIdToken();

            const response = await fetch("/admin-api/admin/api-tokens", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${idToken}`,
                },
                body: JSON.stringify({ name: newTokenName }),
            });

            if (!response.ok) throw new Error("Falha ao criar token");

            const data = await response.json();
            setGeneratedToken(data.token);
            setNewTokenName("");
            fetchTokens();

            // Keep dialog open to show the token
        } catch (error) {
            console.error(error);
            toast({
                title: "Erro",
                description: "Não foi possível criar o token.",
                variant: "destructive",
            });
            setDialogOpen(false);
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteToken = async (id: string) => {
        if (!confirm("Tem certeza que deseja revogar este token? Esta ação não pode ser desfeita.")) return;

        try {
            const user = auth.currentUser;
            if (!user) return;
            const idToken = await user.getIdToken();

            const response = await fetch(`/admin-api/admin/api-tokens/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            });

            if (!response.ok) throw new Error("Falha ao revogar token");

            toast({
                title: "Sucesso",
                description: "Token revogado com sucesso.",
            });
            fetchTokens();
        } catch (error) {
            console.error(error);
            toast({
                title: "Erro",
                description: "Não foi possível revogar o token.",
                variant: "destructive",
            });
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: "Copiado",
            description: "Token copiado para a área de transferência.",
        });
    };

    const toggleVisibility = (id: string) => {
        setVisibleTokens(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return "-";
        // Firestore timestamp or ISO string handling
        const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
        return new Intl.DateTimeFormat("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date);
    };

    return (
        <div className={embedded ? "space-y-6" : "min-h-screen bg-background"}>
            {!embedded && <NavigationHeader />}

            <main className={embedded ? "" : "container mx-auto py-8 space-y-8 px-4"}>
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight text-foreground">Gerenciamento de API</h1>
                        <p className="text-muted-foreground mt-1">
                            Gerencie tokens de acesso para sistemas externos.
                        </p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        if (!open) setGeneratedToken(null);
                        setDialogOpen(open);
                    }}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="mr-2 h-4 w-4" />
                                Novo Token
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle>Gerar Novo Token de API</DialogTitle>
                                <DialogDescription>
                                    Este token permitirá acesso completo aos dados públicos do sistema.
                                </DialogDescription>
                            </DialogHeader>

                            {!generatedToken ? (
                                <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name">Nome do Sistema/Integração</Label>
                                        <Input
                                            id="name"
                                            placeholder="Ex: Painel de Transparência"
                                            value={newTokenName}
                                            onChange={(e) => setNewTokenName(e.target.value)}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4 py-4">
                                    <div className="rounded-md bg-muted p-4 space-y-2">
                                        <div className="flex justify-between items-center">
                                            <Label className="text-xs font-medium text-muted-foreground uppercase">Token de Acesso</Label>
                                            <span className="text-xs text-amber-600 font-medium">Copie agora! Você não verá isso novamente.</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 block w-full rounded bg-background p-2 font-mono text-sm break-all border">
                                                {generatedToken}
                                            </code>
                                            <Button size="icon" variant="ghost" onClick={() => copyToClipboard(generatedToken)}>
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <DialogFooter>
                                {!generatedToken ? (
                                    <Button onClick={handleCreateToken} disabled={isCreating || !newTokenName}>
                                        {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Gerar Token
                                    </Button>
                                ) : (
                                    <Button onClick={() => setDialogOpen(false)}>Concluído</Button>
                                )}
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Tokens Ativos</CardTitle>
                        <CardDescription>Lista de chaves de API ativas e suas estatísticas de uso.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center p-8">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : tokens.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Key className="h-12 w-12 mx-auto mb-4 opacity-20" />
                                <p>Nenhum token de API encontrado.</p>
                            </div>
                        ) : (
                            <div className="rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Nome</TableHead>
                                            <TableHead>Token (Parcial)</TableHead>
                                            <TableHead>Criado em</TableHead>
                                            <TableHead>Último uso</TableHead>
                                            <TableHead className="text-right">Ações</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {tokens.map((token) => (
                                            <TableRow key={token.id}>
                                                <TableCell className="font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <Key className="h-4 w-4 text-muted-foreground" />
                                                        {token.name}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">
                                                    {visibleTokens[token.id] ? token.token : `${token.token.substring(0, 8)}...`}
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 ml-2"
                                                        onClick={() => toggleVisibility(token.id)}
                                                    >
                                                        {visibleTokens[token.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                                    </Button>
                                                </TableCell>
                                                <TableCell>{formatDate(token.createdAt)}</TableCell>
                                                <TableCell>{formatDate(token.lastUsedAt)} ({token.usageCount || 0} reqs)</TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        onClick={() => handleDeleteToken(token.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
